import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { makeStyles } from "@material-ui/core/styles";
import { getFormatedDate } from "../../../../helper";
import { formatToKOrM } from "../../../../helper";

const useStyles = makeStyles((_theme) => ({
  chartContainer: {
    position: "relative",
    overflow: "hidden",
  },
  sliderContainer: {
    margin: "20px 0",
    padding: "0 30px",
    position: "relative",
    maxWidth: "600px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  sliderWrapper: {
    position: "relative",
    height: "8px",
    display: "flex",
    alignItems: "center",
  },
  slider: {
    WebkitAppearance: "none",
    width: "100%",
    height: "4px",
    background: "#e0e0e0",
    borderRadius: "2px",
    outline: "none",
    "&::-webkit-slider-thumb": {
      WebkitAppearance: "none",
      width: "20px",
      height: "20px",
      background: "#0067A1",
      borderRadius: "50%",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      "&:hover": {
        transform: "scale(1.1)",
        background: "#004d80",
      },
      "&:active": {
        transform: "scale(1)",
      },
    },
  },
  pageIndicator: {
    position: "absolute",
    right: "0",
    bottom: "-24px",
    color: "#666",
    fontSize: "0.8rem",
    fontWeight: 400,
    fontFamily: "system-ui, sans-serif",
    "& span": {
      color: "#0067A1",
      fontWeight: 500,
    },
  },
  progressTrack: {
    position: "absolute",
    height: "4px",
    background: "#0067A1",
    borderRadius: "2px",
    transition: "width 0.3s ease",
  },
}));

const BarLineChartForRR = ({ graphData = [], activeTab, type }) => {
  const classes = useStyles();
  const [borderRadius, setBorderRadius] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5;

  useEffect(() => {
    setCurrentPage(0);
  }, [activeTab]);

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 1000) {
        setBorderRadius(2);
      } else if (screenWidth < 1500) {
        setBorderRadius(5);
      } else {
        setBorderRadius(7);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const totalItems = graphData?.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // Data mapping based on `activeTab`
  let seriesData = [];
  let yAxisTitle = "";

  let categories = [];

  if (activeTab === "Timeline") {
    categories = graphData?.map((item) => item?.Year_week || "Unknown Metric");
    seriesData = [
      {
        name: `# ${type} `,
        type: "bar",
        data: graphData?.map((item) => item?.Value || 0),
      },
      {
        name: "Order Value ($ Millions)",
        type: "line",
        data: graphData?.map((item) => item?.Revenue || 0),
      },
    ];
    yAxisTitle = "Count";
  }
  // Slice data for current page
  const slicedCategories = categories?.slice(startIndex, endIndex);
  const slicedSeries = seriesData?.map((s) => ({
    ...s,
    data: s?.data?.slice(startIndex, endIndex),
  }));

  // Chart options
  const options = {
    chart: {
      height: 350,
      type: "bar",
      stacked: false,
      toolbar: { show: false },
      animations: {
        enabled: false,
        easing: "easeInOutQuad",
        speed: 600,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "20%",
        borderRadius: borderRadius,
        borderRadiusApplication: "end",
      },
    },
    dataLabels: { enabled: false },
    stroke: { width: 1 },
    series: slicedSeries,
    colors: ["#EC6FBD", "#A4D65D", "#01B2AA", "#0067A1"],
    xaxis: {
      categories: slicedCategories,
    },
    yaxis: [
      {
        title: {
          text: yAxisTitle,
          style: {
            fontSize: "12px",
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            color: "#333",
          },
        },
        labels: {
          formatter: (value) => value,
        },
      },
      {
        // Right y-axis for accuracy (percentage)
        opposite: true, // Place the axis on the right side
        title: {
          text: "$ Millions",
          style: {
            fontSize: "12px",
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            color: "#333",
          },
        },
        labels: {
          formatter: (value) => `$${formatToKOrM(value)}`,
        },
      },
    ],
    tooltip: {
      shared: true,
      intersect: false,
      y: [
        {
          formatter: (value) => value,
        },
        {
          formatter: (value) => `$${formatToKOrM(value)}`,
        },
      ],
    },
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      markers: {
        width: 12,
        height: 12,
        radius: 10,
        shape: "circle",
      },
    },
    grid: { show: false },
    noData: {
      text: "No Data Available",
      align: "center",
      verticalAlign: "middle",
      style: {
        color: "#404041",
        fontSize: "14px",
        fontFamily: "Poppins, sans-serif",
      },
    },
  };

  return (
    <div className="grouped-bar-chart">
      <ReactApexChart
        options={options}
        series={slicedSeries}
        type="bar"
        height={350}
      />
      {totalPages > 1 && (
        <div className={classes.sliderContainer}>
          <div className={classes.sliderWrapper}>
            <div
              className={classes.progressTrack}
              style={{
                width: `${((currentPage + 1) / totalPages) * 100}%`,
                opacity: 0.3,
              }}
            />
            <input
              type="range"
              min="0"
              max={totalPages - 1}
              value={currentPage}
              onChange={(e) => setCurrentPage(parseInt(e.target.value, 10))}
              className={classes.slider}
            />
          </div>
          <div className={classes.pageIndicator}>
            <span>{currentPage + 1}</span> / {totalPages}
          </div>
        </div>
      )}
    </div>
  );
};

export default BarLineChartForRR;
